import { useState, useCallback } from 'react';
import { FileJson, Upload, Copy, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';

const CsvToJson = () => {
  useToolUsage('csv-json');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'csv-to-json' | 'json-to-csv'>('csv-to-json');

  const csvToJson = (csv: string): string => {
    const lines = csv.trim().split('\n');
    if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const result = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj: Record<string, string> = {};
      headers.forEach((h, i) => { obj[h] = values[i] || ''; });
      return obj;
    });
    return JSON.stringify(result, null, 2);
  };

  const jsonToCsv = (json: string): string => {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr) || arr.length === 0) throw new Error('JSON must be an array of objects');
    const headers = Object.keys(arr[0]);
    const rows = arr.map((obj: any) => headers.map(h => `"${String(obj[h] ?? '').replace(/"/g, '""')}"`).join(','));
    return [headers.join(','), ...rows].join('\n');
  };

  const convert = useCallback(() => {
    if (!input.trim()) { toast.error('Please enter some input'); return; }
    try {
      const result = mode === 'csv-to-json' ? csvToJson(input) : jsonToCsv(input);
      setOutput(result);
      toast.success('Converted!');
    } catch (e: any) {
      toast.error(e.message || 'Conversion failed');
    }
  }, [input, mode]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setInput(reader.result as string);
    reader.readAsText(f);
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    toast.success('Copied!');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <FileJson className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">CSV ↔ JSON Converter</h1>
          <p className="text-muted-foreground">Convert between CSV and JSON formats</p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant={mode === 'csv-to-json' ? 'default' : 'outline'} onClick={() => { setMode('csv-to-json'); setOutput(''); }}>CSV → JSON</Button>
        <Button size="sm" variant={mode === 'json-to-csv' ? 'default' : 'outline'} onClick={() => { setMode('json-to-csv'); setOutput(''); }}>JSON → CSV</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between">
              <Label>{mode === 'csv-to-json' ? 'CSV Input' : 'JSON Input'}</Label>
              <label className="cursor-pointer">
                <Button size="sm" variant="ghost" asChild><span><Upload className="h-3 w-3 mr-1" /> Upload</span></Button>
                <input type="file" accept=".csv,.json,.txt" onChange={handleFile} className="hidden" />
              </label>
            </div>
            <Textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={mode === 'csv-to-json' ? 'name,age,city\nAlice,30,NYC\nBob,25,LA' : '[{"name":"Alice","age":30}]'}
              className="min-h-[300px] font-mono text-sm"
            />
            <Button onClick={convert} className="w-full">
              <ArrowRightLeft className="h-4 w-4 mr-2" /> Convert
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between">
              <Label>{mode === 'csv-to-json' ? 'JSON Output' : 'CSV Output'}</Label>
              <Button size="sm" variant="ghost" onClick={copy} disabled={!output}><Copy className="h-3 w-3 mr-1" /> Copy</Button>
            </div>
            <Textarea value={output} readOnly className="min-h-[300px] font-mono text-sm bg-muted/30" placeholder="Result will appear here..." />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CsvToJson;
